import { DateTime } from 'luxon';
import * as React from 'react';
import "react-widgets/styles.css";
import {
    Col,
    FormGroup,
    Label,
    Row
} from 'reactstrap';
import { useAppDispatch, useAppSelector } from '../hooks';
import { EntityReference, getAllEntities, selectEntities } from "../redux/EntitiesSlice";
import {
    DoingBusinessAs, Entity, EntityInput, EntityReportAccessChangeInput, getSelectedEntity,
    saveEntityDetail, selectedEntityAddress
} from '../redux/EntitySlice';
import { Part, Reference } from '../redux/TaxonomyReferenceSlice';
import { ActiveTaxonomyVersionInfo, selectCurrentVersion } from '../redux/TaxonomyVersionSlice';
import Page from './Page';
import EntityContactsManager from './feature/EntityContactsManager';




const DepartmentContactManagement = () => {

    const dispatch = useAppDispatch();
    const entityAddressSelector = useAppSelector(selectedEntityAddress);
    const entitySelector: Entity | null = useAppSelector(getSelectedEntity);
    const selectedVersion: ActiveTaxonomyVersionInfo | null = useAppSelector(selectCurrentVersion);
    const entitiesSelector: EntityReference[] | null = useAppSelector(selectEntities);

    //fiscal due date hooks
    const [fiscalDay, setFiscalDay] = React.useState<number | null>(null);
    const [fiscalMonth, setFiscalMonth] = React.useState<number | null>(null);

    //entity status hooks
    const [dissolvedDate, setDissolvedDate] = React.useState<string | null>(null);
    const [entityStatus, setEntityStatus] = React.useState<string | null>(null);
    const [inactiveDate, setInactiveDate] = React.useState<string | null>(null);
    const [reactivateDate, setReactivateDate] = React.useState<string | null>(null);
    const [statusReason, setStatusReason] = React.useState<string | null>(null);
    const [renamedDate, setRenamedDate] = React.useState<string | null>(null);
    const [mergedDate, setMergedDate] = React.useState<string | null>(null);

    //address hooks
    const [street, setStreet] = React.useState<string | null>(null);
    const [zipCode, setZipCode] = React.useState<string | null>(null);
    const [pobox, setPobox] = React.useState<string | null>(null);
    const [state, setState] = React.useState<string | null>(null);
    const [county, setCounty] = React.useState<string | null>(null);
    const [cityCode, setCityCode] = React.useState<string | null>(null);

    //visual hook
    const [activeTab, setActiveTab] = React.useState('1');
    const [isEntityUpdateSaving, setIsEntityUpdateSaving] = React.useState<boolean>(false);

    //error hook
    const [macroError, setMacroError] = React.useState<any>();

    //label hooks
    const [entityName, setEntityName] = React.useState<string>('');
    const [entityTypeCode, setEntityTypeCode] = React.useState<string>('');
    const [districtType, setDistrictType] = React.useState<string | null>(null);
    const [entityCode, setEntityCode] = React.useState<string | null>('');
    const [createDate, setCreateDate] = React.useState<string | null>(null);
    const [primaryGovernment, setPrimaryGovernment] = React.useState<string | null>(null);
    const [districtDependency, setDistrictDependency] = React.useState<string | null>(null);


    const defaultOption: { value: string; label: string } = { value: "0", label: "Select..." };

    //report access hooks
    const [parentAfrEnabled, setParentAfrEnabled] = React.useState<boolean>(false);
    const [parentCourtReporting939Enabled, setParentCourtReporting939Enabled] = React.useState<boolean>(false);
    const [parentCourtReporting29Enabled, setParentCourtReporting29Enabled] = React.useState<boolean>(false);

    const [parentAfrReportAccessDate, setParentAfrReportAccessDate] = React.useState<any>(null);
    const [parentNineThirtyNineReportAccessDate, setParentNineThirtyNineReportAccessDate] = React.useState<any>(null);
    const [parentTwentyNineReportAccessDate, setParentTwentyNineReportAccessDate] = React.useState<any>(null);

    const [parentReportAccessHasChanged, setParentReportAccessHasChanged] = React.useState<boolean>(false);


    //references
    const [creationDocumentData, setCreationDocumentData] = React.useState<Array<string>>([]);
    const [statoryAuthorityData, setStatoryAuthorityData] = React.useState<Array<string>>([]);

    //doing business as
    const [doingBusinessAs, setDoingBusinessAs] = React.useState<"Previously Doing Business As" | "Currently Doing Business As" | "">("");

    const [shouldShowDoingBusinessAs, setShouldShowDoingBusinessAs] = React.useState<Boolean>(false);

    const [doingBusinessAsOptions, setDoingBusinessAsOptions] = React.useState<Array<{ value: string; label: string; }>>([]);

    const [selectedDoingBusinessAs, setSelectedDoingBusinessAs]
        = React.useState<{ value: string; label: string }>(defaultOption);


    function initEntityReferenceGivenSelector(setReferenceHook: React.Dispatch<React.SetStateAction<string[]>>, referenceType: string) {

        if (entitySelector && entitySelector.references) {
            if (entitySelector.references.length > 0) {
                let result: Array<string> = [];

                var referenceNodeList = entitySelector.references.filter((referenceNode: Reference) => referenceNode.type.indexOf(referenceType) > 0);

                if (referenceNodeList.length > 0) {

                    for (var i = 0; i < referenceNodeList.length; i++) {

                        let workingLabel: string = "";
                        var namePartNode = referenceNodeList[i].parts.find((element: Part) => element.definition.name.toLowerCase() == "name");

                        if (namePartNode) {
                            workingLabel += namePartNode.value + ": ";
                        }

                        var remainingPartNodes = referenceNodeList[i].parts.filter((element: Part) => element.definition.name.toUpperCase() != "NAME"
                            && element.definition.name.toUpperCase() != "UASID");

                        for (var j = 0; j < remainingPartNodes.length; j++) {
                            workingLabel += remainingPartNodes[j].definition.name + " " + remainingPartNodes[j].value + "<br />";
                        }

                        result.push(workingLabel);
                    }

                    setReferenceHook(result);
                }
            }
        }
    }



    const handleDoingBusinessAsChange = (selectItem: { value: string; label: string }) => {
        setSelectedDoingBusinessAs(selectItem);
    }

    const toggle = (tab: any) => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    function getDoingBusinessAsAffiliation(): DoingBusinessAs | null {
        let result: DoingBusinessAs | null = null;

        if (entitySelector) {
            if (selectedDoingBusinessAs.label != defaultOption.label) {
                switch (doingBusinessAs) {

                    case "Currently Doing Business As":
                        result = {
                            currentEntity: selectedDoingBusinessAs.value,
                            previousEntity: entitySelector.code
                        }
                        break;

                    case "Previously Doing Business As":
                        result = {
                            previousEntity: selectedDoingBusinessAs.value,
                            currentEntity: entitySelector.code
                        }
                        break;

                    default:
                        break;
                }
            }
        }

        return result;
    }

    const saveEntity = async () => {
        setIsEntityUpdateSaving(true);
        var convertedDissolvedDate;
        if (dissolvedDate && dissolvedDate.length > 0) {
            convertedDissolvedDate = DateTime.fromISO(dissolvedDate);
        }
        else {
            convertedDissolvedDate = null;
        }

        var convertedInactiveDate;
        if (inactiveDate && inactiveDate.length > 0) {
            convertedInactiveDate = DateTime.fromISO(inactiveDate);
        }
        else {
            convertedInactiveDate = null;
        }

        var convertedReactivateDate;
        if (reactivateDate && reactivateDate.length > 0) {
            convertedReactivateDate = DateTime.fromISO(reactivateDate);
        }
        else {
            convertedReactivateDate = null;
        }

        var convertedRenamedDate;
        if (renamedDate && renamedDate.length > 0) {
            convertedRenamedDate = DateTime.fromISO(renamedDate);
        }
        else {
            convertedRenamedDate = null;
        }

        var convertedMergedDate;
        if (mergedDate && mergedDate.length > 0) {
            convertedMergedDate = DateTime.fromISO(mergedDate);
        }
        else {
            convertedMergedDate = null;
        }

        let entityReportAccessChangesFromUI: EntityReportAccessChangeInput[] = [{
            state: 'AFR_ENABLED',
            value: parentAfrEnabled,
            timestamp: parentAfrReportAccessDate ? parentAfrReportAccessDate : null
        },
        {
            state: 'COURT_REPORTING_939_ENABLED',
            value: parentCourtReporting939Enabled,
            timestamp: parentNineThirtyNineReportAccessDate ? parentNineThirtyNineReportAccessDate : null
        },
        {
            state: 'COURT_REPORTING_29_ENABLED',
            value: parentCourtReporting29Enabled,
            timestamp: parentTwentyNineReportAccessDate ? parentTwentyNineReportAccessDate : null
        }];

        // NOTE - the name is now drawn from the local state rather than being re-fetched from the entity. This allows for the user to change its value.
        let entityToSave: EntityInput = {
            entityId: entitySelector !== null ? entitySelector.code : "",
            fiscalEndMonth: fiscalMonth,
            fiscalEndDay: fiscalDay,
            dissolvedDate: convertedDissolvedDate,
            inactiveDate: convertedInactiveDate,
            renamedDate: convertedRenamedDate,
            mergedDate: convertedMergedDate,
            createDate: convertedReactivateDate,
            affiliation: null,
            status: entityStatus,
            statusReason: statusReason && statusReason.length > 0 ? statusReason : null,
            address: {
                street: street,
                pobox: pobox,
                cityId: cityCode,
                zip: zipCode,
            },
            name: entitySelector !== null ? entityName : "",
            nameChangeDate: null,
            typecode: entitySelector !== null ? entitySelector.typecode : "",
            districttype: entitySelector != null ? entitySelector.districttype : "",
            taxonomyYear: selectedVersion ? selectedVersion.year : null,
            entityReportAccessChangeInput: parentReportAccessHasChanged == true ? entityReportAccessChangesFromUI : null,
            doingBusinessAs: getDoingBusinessAsAffiliation(),
            // Departments don't support district dependencies
            districtDependency: null,
            districtDependencyChangeDate: null,
            servesInCounties: null
        }

        await dispatch(saveEntityDetail(entityToSave)).then((resultAction: any) => {
            if (saveEntityDetail.rejected.match(resultAction)) {
                setMacroError(resultAction.error);
            }
            else {
                setMacroError(null);
            }
        });
        setIsEntityUpdateSaving(false);
    }

    function initDoingBusinessAsOptions(entitiesSelector: EntityReference[] | null, doingBusinessAs: string, setDoingBusinessAsOptions: React.Dispatch<React.SetStateAction<{ value: string; label: string; }[]>>) {
        if (entitiesSelector) {

            var result: Array<{ value: string; label: string; }> = [];

            let entityListWithStatus: EntityReference[] = [];

            result.push({ label: "Remove Relationship", value: "0" });

            switch (doingBusinessAs) {
                case "Currently Doing Business As":
                    entityListWithStatus = entitiesSelector.filter((element: EntityReference) => element.status == "ACTIVE");
                    for (var i = 0; i < entityListWithStatus.length; i++) {
                        result.push({ value: entityListWithStatus[i].code, label: entityListWithStatus[i].code + " - " + entityListWithStatus[i].name });
                    }

                    break;
                case "Previously Doing Business As":
                    entityListWithStatus = entitiesSelector.filter((element: EntityReference) => element.status == "DISSOLVED"
                        || element.status == "INACTIVE" || element.status == "RENAMED" || element.status == "MERGED");
                    for (var i = 0; i < entityListWithStatus.length; i++) {
                        result.push({
                            value: entityListWithStatus[i].code, label: entityListWithStatus[i].code
                                + " - " + entityListWithStatus[i].name + " (" + entityListWithStatus[i].status + ")"
                        });
                    }

                    break;

                default:
                    entityListWithStatus = [];
                    setDoingBusinessAsOptions([]);
                    setSelectedDoingBusinessAs(defaultOption);
                    break;
            }

            if (result.length > 1) {
                setDoingBusinessAsOptions(result);
                let codeValue: string | null = null;

                if (entitySelector && entitySelector.doingBusinessAs) {

                    let currentCodeValue: string | null = entitySelector.doingBusinessAs.currentEntity;
                    let previousCodeValue: string | null = entitySelector.doingBusinessAs.previousEntity;

                    if (entitySelector.code == currentCodeValue) {
                        codeValue = entitySelector.doingBusinessAs.previousEntity;
                    } else if (entitySelector.code == previousCodeValue) {
                        codeValue = entitySelector.doingBusinessAs.currentEntity;
                    }

                    var foundRelatingListItem = result.find((element: { value: string; label: string; }) => element.value == codeValue);

                    if (foundRelatingListItem) {
                        handleDoingBusinessAsChange(foundRelatingListItem);
                    } else {
                        handleDoingBusinessAsChange(defaultOption);
                    }
                } else {
                    handleDoingBusinessAsChange(defaultOption);
                }
            }

        } else {
            setDoingBusinessAsOptions([]);
        }
    }


    React.useEffect(() => {
        if (entityAddressSelector) {
            if (entityAddressSelector.street) {
                setStreet(entityAddressSelector.street);
            }
            if (entityAddressSelector.zip) {
                setZipCode(entityAddressSelector.zip);
            }
            if (entityAddressSelector.pobox) {
                setPobox(entityAddressSelector.pobox);
            }
            if (entityAddressSelector.city &&
                entityAddressSelector.city.code) {
                setCityCode(entityAddressSelector.city.code);
            }
            setState(null);
            setCounty(null);

        }
        else {
            setStreet(null);
            setZipCode(null);
            setPobox(null);
            setState(null);
            setCounty(null);
            setCityCode(null);
        }
    }, [entityAddressSelector])

    React.useEffect(() => {

        setCreationDocumentData([]);
        setStatoryAuthorityData([]);

        if (entitySelector) {
            setDissolvedDate(entitySelector.dissolvedDate);
            setInactiveDate(entitySelector.inactiveDate);
            setReactivateDate(entitySelector.creationDate);
            setRenamedDate(entitySelector.renamedDate);
            setMergedDate(entitySelector.mergedDate);
            setFiscalMonth(entitySelector.fiscalEndMonth);
            setFiscalDay(entitySelector.fiscalEndDay);
            setEntityStatus(entitySelector.status);
            setEntityName(entitySelector.name);
            setEntityTypeCode(entitySelector.typecode);
            setDistrictType(entitySelector.districttype);
            setCreateDate(DateTime.fromISO(entitySelector.creationDate).toFormat('LL/dd/yyyy'));

            setEntityCode(entitySelector.code);
            if (entitySelector.affiliation) {
                setPrimaryGovernment(entitySelector.affiliation.affiliate.name);
                setDistrictDependency(entitySelector.districtDependency);
            }

            initEntityReferenceGivenSelector(setCreationDocumentData, "/creation");
            initEntityReferenceGivenSelector(setStatoryAuthorityData, "/authority");
            setStatusReason(entitySelector.statusReason);
        }
        else {
            setDissolvedDate(null);
            setInactiveDate(null);
            setFiscalMonth(null);
            setReactivateDate(null);
            setRenamedDate(null);
            setMergedDate(null);
            setFiscalDay(null);
            setEntityStatus(null);
            setEntityName("");
            setEntityTypeCode("");
            setDistrictType(null);
            setEntityCode(null);
            setCreateDate(null);
            setPrimaryGovernment(null);
            setDistrictDependency(null);
            setStatusReason("");
        }

        initDoingBusinessAsOptions(entitiesSelector, doingBusinessAs, setDoingBusinessAsOptions);

    }, [entitySelector]);

    React.useEffect(() => {

        if (entityStatus) {
            if (entityStatus == "DISSOLVED" || entityStatus == "INACTIVE" || entityStatus == "RENAMED" || entityStatus == "MERGED") {
                setDoingBusinessAs("Currently Doing Business As");
                setShouldShowDoingBusinessAs(true);
            }
            else if (entityStatus == "ACTIVE") {
                setDoingBusinessAs("Previously Doing Business As");
                setShouldShowDoingBusinessAs(true);
            } else {
                setDoingBusinessAs("");
                setShouldShowDoingBusinessAs(false);
            }
        } else {
            setDoingBusinessAs("");
            setShouldShowDoingBusinessAs(false);
        }

    }, [entityStatus])

    React.useEffect(() => {

        initDoingBusinessAsOptions(entitiesSelector, doingBusinessAs, setDoingBusinessAsOptions);

    }, [doingBusinessAs])

    React.useEffect(() => {

        dispatch(getAllEntities);

    }, [saveEntity])

    const creationDocumentLabelRender = creationDocumentData.length > 0 ? (<React.Fragment>
        <FormGroup>
            <Row>
                <Col md="4" style={{ textAlign: "left" }}>
                    <Label>Creation Documents:</Label>
                </Col>
                <Col md="8">
                    <ul>
                        {creationDocumentData.map((element: string, index) => (
                            <li key={index}>
                                <div dangerouslySetInnerHTML={{ __html: element }} ></div>
                            </li>
                        ))}
                    </ul>
                </Col>
            </Row>
        </FormGroup>

    </React.Fragment>) : (<></>);

    const statutoryAuthorityLabelRender = statoryAuthorityData.length > 0 ? (<React.Fragment>
        <FormGroup>
            <Row>
                <Col md="4" style={{ textAlign: "left" }}>
                    <Label>Statutory Authority:</Label>
                </Col>
                <Col md="8">
                    <ul>
                        {statoryAuthorityData.map((element: string, index) => (
                            <li key={index}>
                                <div dangerouslySetInnerHTML={{ __html: element }} ></div>
                            </li>
                        ))}
                    </ul>
                </Col>
            </Row>
        </FormGroup>
    </React.Fragment>) : (<></>);

    return (
        <Page error={macroError} setError={setMacroError}>
            <Row>
                <Col sm="12">
                    <EntityContactsManager isStaff={true} />
                </Col>
            </Row>
        </Page>
    );
}

export default DepartmentContactManagement;



